import { useEffect, useRef, useSyncExternalStore } from "react";

/** Which action sheet is up on the gaps-first home. */
export enum GapsSheetKind {
	PostShift = "post-shift",
	AddStaff = "add-staff",
	StaffActions = "staff-actions",
	AssignUnit = "assign-unit",
	CallIn = "call-in",
	PostedActions = "posted-actions",
	ApproveConfirm = "approve-confirm",
}

/** Why the assign-unit sheet opened — one sheet serves all three (P3-6), and its analytics say which. */
export enum GapsAssignUnitReason {
	StaffRow = "staff_row",
	Approve = "approve",
	Add = "add",
}

/**
 * The hole an add was started from, captured at the tap (P3-8): the group by key, and the unit and hours the
 * missing row named. Captured by VALUE because a missing row's own key is positional — the unit's second hole
 * becomes its first once someone fills one — while the unit and window the user chose must not move under them.
 */
export type GapsAddStaffScope = { groupKey: string; locationName: string; startMin: number; endMin: number };

/**
 * The staffing day a home sheet acts on, as the home keys its URL day (`yyyy-MM-dd`). Every sheet that reads or writes
 * the home's day — a group, a shift, a hole, the day's staff — carries the day it was opened for, so a sheet can never
 * act on a day the user did not open it on: the home's day can move under an open sheet (the bar's Gaps slot lands on
 * the undated home, the phone's back button steps the URL), and a group or a hole is keyed without a day.
 */
type DayBound = { dayISO: string };

/**
 * What a sheet was opened for. Anything that can change while it is open — a group's counts, a shift, a request —
 * is carried by id or key and read fresh from the page's data, never snapshotted here. `null` fields are the
 * unscoped openings: the bottom bar's Post and More's quick actions start from nothing.
 *
 * The posted-request sheets carry no day: a request carries its own, and they act on it wherever it is listed.
 */
export type GapsSheetIntent =
	| ({ kind: GapsSheetKind.PostShift; groupKey: string | null } & DayBound)
	| ({
			kind: GapsSheetKind.AddStaff;
			scope: GapsAddStaffScope | null;
			linkedRequestId: string | null;
			/**
			 * An unscoped opening that already knows its shift — the post sheet's "Add staff directly" — starts on that
			 * group's people rather than on the shift picker. `null` asks which shift first.
			 */
			groupKey: string | null;
			/**
			 * The page backing out returns to, when the add was opened from another page over the home: Open Shifts, for a
			 * posted shift's "Add someone directly". `null` for an add opened on the home — backing out stays there.
			 */
			backPath: string | null;
	  } & DayBound)
	| ({ kind: GapsSheetKind.StaffActions; shiftId: string } & DayBound)
	| ({ kind: GapsSheetKind.AssignUnit; shiftId: string; reason: GapsAssignUnitReason } & DayBound)
	| ({ kind: GapsSheetKind.CallIn } & DayBound)
	| { kind: GapsSheetKind.PostedActions; requestId: string }
	| { kind: GapsSheetKind.ApproveConfirm; requestId: string; nurseId: number };

/** One kind's intent — the target a sheet of that kind is handed. */
export type GapsSheetIntentOf<K extends GapsSheetKind> = Extract<GapsSheetIntent, { kind: K }>;

/**
 * The intent a home on `dayISO` shows: a sheet opened for another day is not this home's to draw. It is the same-render
 * half of the day rule — the store half is `useCloseGapsSheetOffDay` — so a sheet never draws, even for a frame, over a
 * day it was not opened on. `null` passes through.
 */
export const intentForHomeDay = (intent: GapsSheetIntent | null, dayISO: string): GapsSheetIntent | null => {
	if (intent === null || !("dayISO" in intent) || intent.dayISO === dayISO) return intent;
	// A page moving to the day the home just landed on (`moveGapsSheetToDay`) is drawn there before the store catches up.
	return pendingDayMove !== null && pendingDayMove.from === intent && pendingDayMove.toDayISOs.includes(dayISO) ? intent : null;
};

/**
 * The one open intent, module-level, and everything reading it.
 *
 * The sheets live in `MobileGapsStaffing`, but two of their triggers do not: the bottom bar's Post slot and More's
 * quick actions sit in the layout, in a different subtree, and may fire from another route. React state in either
 * place could not reach the other; an external store makes one write visible to every reader in the same commit —
 * the pattern `useGapsViewPreference` uses for the view choice. It also lets the bar light its Post slot while the
 * post sheet is open.
 *
 * It outlives the page on purpose: a trigger on `/requests` opens the intent and then navigates home, and the page
 * that mounts must find it. The flip side is the host's job: the page that renders the sheets has to close the
 * intent when it unmounts, or a sheet left open would reappear on the next visit — another department's included.
 */
let currentIntent: GapsSheetIntent | null = null;
const subscribers = new Set<() => void>();

/**
 * A day-bound sheet asked to follow the home to another day (`moveGapsSheetToDay`), until the home lands there. Kept
 * outside the intent because the home lands a render AFTER the request: React Router runs its navigation as a
 * transition, and an intent re-dated first would be drawn — and closed — over the day the home is still on.
 *
 * Every day the page asked for, the last one its target: two quick picks can land the first after the second was asked
 * for, and the page follows that landing too rather than closing on it.
 */
let pendingDayMove: { from: GapsSheetIntent; toDayISOs: readonly string[] } | null = null;

/** Each re-dated intent → the intent its opening began as, so the moved sheet stays the same opening (`openingOriginOf`). */
const openingOrigins = new WeakMap<object, object>();

/**
 * The intent an opening began as: itself, or — for an intent re-dated by `moveGapsSheetToDay` — the one it was opened
 * as. `useSheetOpening` keys a sheet's opening by this, so a page that changes its day keeps what was typed in it.
 */
export const openingOriginOf = <T extends object>(intent: T): T => {
	const origin = openingOrigins.get(intent);
	// DEFAULT: an intent that was never re-dated is its own origin. A re-dated one was built by spreading its origin
	// (`useCloseGapsSheetOffDay`), so the origin is the same kind of intent as `T` — the map only loses that type.
	return origin === undefined ? intent : (origin as T);
};

const notifySubscribers = (): void => {
	for (const onChange of subscribers) onChange();
};

const subscribe = (onChange: () => void): (() => void) => {
	subscribers.add(onChange);
	return () => {
		subscribers.delete(onChange);
	};
};

/** The open intent, or `null` when no sheet is up. Stable between writes, as `useSyncExternalStore` requires. */
export const getGapsSheetIntent = (): GapsSheetIntent | null => currentIntent;

/** Opens a sheet, replacing whichever was up — the home shows one action sheet at a time. */
export const openGapsSheet = (intent: GapsSheetIntent): void => {
	currentIntent = intent;
	pendingDayMove = null;
	notifySubscribers();
};

/** Closes whichever sheet is up. Closing when none is up is a no-op and re-renders nobody. */
export const closeGapsSheet = (): void => {
	if (currentIntent === null) return;
	currentIntent = null;
	pendingDayMove = null;
	notifySubscribers();
};

/**
 * Lets the day-bound sheet `from` follow the home to `toDayISO` instead of closing when the home moves there — a page's
 * own date picker (APP-886: Post a shift, Add someone). The caller then navigates the home; once it lands, the intent is
 * re-dated as the SAME opening, so the page keeps everything typed in it. `false` when `from` is no longer up, or is not
 * bound to a day.
 */
export const moveGapsSheetToDay = (from: GapsSheetIntent, toDayISO: string): boolean => {
	if (currentIntent !== from || !("dayISO" in from)) return false;
	pendingDayMove = { from, toDayISOs: pendingDayMove !== null && pendingDayMove.from === from ? [...pendingDayMove.toDayISOs, toDayISO] : [toDayISO] };
	return true;
};

/**
 * Closes the sheet only while `intent` is still the one up. A sheet's own close is this, never `closeGapsSheet`: a
 * write that finishes after the user moved on — to another sheet from the bar, or to none — must not close the sheet
 * they moved to.
 */
export const closeGapsSheetIfCurrent = (intent: GapsSheetIntent): void => {
	if (currentIntent === intent) closeGapsSheet();
};

/**
 * Hands `from` on to `next` (an add's assign-unit, an approval's) only while `from` is still up; `false` when the
 * user already moved on, and nothing was opened.
 */
export const replaceGapsSheetIfCurrent = (from: GapsSheetIntent, next: GapsSheetIntent): boolean => {
	if (currentIntent !== from) return false;
	openGapsSheet(next);
	return true;
};

export type GapsSheetController = {
	intent: GapsSheetIntent | null;
	open: (intent: GapsSheetIntent) => void;
	close: () => void;
};

/** The open sheet intent for any component, re-rendering it when a sheet opens, changes or closes. */
export const useGapsSheet = (): GapsSheetController => {
	const intent = useSyncExternalStore(subscribe, getGapsSheetIntent);
	return { intent, open: openGapsSheet, close: closeGapsSheet };
};

/**
 * The host's half of the bargain above: closes whichever sheet is up once the page that renders the sheets unmounts
 * for real, so a sheet left open never reappears on the next visit — or in another department, whose home is a new
 * mount (`MobileGapsShell` keys it by department). A host that renders only some kinds (Open Shifts: the posted-shift
 * and approve sheets) passes them, and closes only an intent of its own.
 *
 * Deferred by a microtask and re-checked, because a plain cleanup would also fire on StrictMode's simulated unmount
 * (`index.tsx` renders under `StrictMode`): React tears the effect down and runs it again synchronously, and a close
 * there would drop the intent a trigger on another route opened before navigating home — exactly the case the store
 * outlives the page for. The ref survives the simulated remount, so only an unmount that is never followed by a
 * remount closes the sheet.
 */
export const useCloseGapsSheetOnUnmount = (ownedKinds?: readonly GapsSheetKind[]): void => {
	const isMountedRef = useRef<boolean>(false);
	// Pass a module-level constant: a new list per render would re-run the effect (harmlessly — the re-check below keeps
	// the sheet — but pointlessly).
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			queueMicrotask(() => {
				if (isMountedRef.current) return;
				// A host that renders only some kinds leaves the others alone: Open Shifts hands an approval to the home's
				// assign-unit sheet and navigates there, and its own unmount must not close the sheet it just opened.
				if (ownedKinds !== undefined && (currentIntent === null || !ownedKinds.includes(currentIntent.kind))) return;
				closeGapsSheet();
			});
		};
	}, [ownedKinds]);
};

/**
 * The store half of the day rule (see `DayBound`): once the home's day CHANGES under an open sheet that was opened for
 * another day, that sheet is closed — unless it asked to follow the home there (`moveGapsSheetToDay`), when it is re-dated
 * instead — so returning to its day does not bring it back, and nothing acts on a day the user
 * left. Only a change counts: an intent opened for a day the home is navigating to (an approval handing on to the
 * request's day, a quick action from another route) is written BEFORE the navigation lands, and must survive until it
 * does.
 */
export const useCloseGapsSheetOffDay = (dayISO: string): void => {
	const shownDayRef = useRef<string>(dayISO);
	useEffect(() => {
		if (shownDayRef.current === dayISO) return;
		shownDayRef.current = dayISO;
		if (currentIntent === null || !("dayISO" in currentIntent) || currentIntent.dayISO === dayISO) return;
		if (pendingDayMove !== null && pendingDayMove.from === currentIntent && pendingDayMove.toDayISOs.includes(dayISO)) {
			// The page asked to come along: re-date it as the same opening rather than closing it. An earlier pick landing
			// after a later one was asked for (two quick picks) is followed too, and the page keeps waiting for the last.
			const moved: GapsSheetIntent = { ...currentIntent, dayISO };
			openingOrigins.set(moved, openingOriginOf(currentIntent));
			const isLastAsked = pendingDayMove.toDayISOs[pendingDayMove.toDayISOs.length - 1] === dayISO;
			pendingDayMove = isLastAsked ? null : { from: moved, toDayISOs: pendingDayMove.toDayISOs };
			currentIntent = moved;
			notifySubscribers();
			return;
		}
		closeGapsSheet();
	}, [dayISO]);
};
